import { useMemo, useState } from "react";
import { useAuth } from "@/context/auth.context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import {
  HandCoins,
  CreditCard,
  Calendar,
  Clock,
  PiggyBank,
  ArrowRight,
  MapPin,
  CheckCircle,
  XCircle,
  Hourglass,
  FileText,
  Download,
  Plus,
  AlertTriangle,
  Users,
  ChevronRight,
  Info,
  Layers,
  WalletCards,
  ReceiptText,
  UserCircle,
  History,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigate, useNavigate } from "react-router-dom";
import { format, endOfMonth, formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import DataTable from "@/components/datatable/Datatable";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import type { ColumnDef, ColumnFiltersState, PaginationState, SortingState } from "@tanstack/react-table";
import { SHARE_VALUE, calculateShares } from "@/utils/shares";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function MemberDashboard() {
  const { user, portalMode } = useAuth();
  const navigate = useNavigate();
  const [recentActivitySearch, setRecentActivitySearch] = useState("");
  const [recentActivityColumnFilters, setRecentActivityColumnFilters] = useState<ColumnFiltersState>([]);
  const [recentActivitySorting, setRecentActivitySorting] = useState<SortingState>([
    { id: "date", desc: true },
  ]);
  const [recentActivityPagination, setRecentActivityPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });

  console.log("[MemberDashboard] Logged-in user:", {
    id: user?.id,
    email: user?.email,
    name: user?.name || user?.fullName,
    type: user?.type,
    group: user?.group?.name,
    leadershipRole: user?.leadershipRole,
    portalMode,
  });

  const getLastName = (u: any): string => {
    if (u?.lastName) return u.lastName;
    if (u?.fullName) {
      const parts = u.fullName.trim().split(/\s+/);
      return parts[0];
    }
    const nameField = u?.name || u?.names;
    if (nameField) {
      const parts = nameField.trim().split(/\s+/);
      return parts[0];
    }
    return "Member";
  };

  const isMemberPortal = portalMode === "member";

  const {
    data: settingsData,
    isLoading: settingsLoading,
  } = useQuery(
    ["member-dashboard-settings"],
    async () => {
      const { data } = await api.get("/settings");
      return data.data || {};
    },
    {
      enabled: !!user?.id,
      staleTime: 60000,
      onError: (err: any) => {
        console.error("[MemberDashboard] Settings fetch error:", err);
      },
    }
  );

  const configuredShareValue = Number(settingsData?.savings?.shareValue) || 0;
  const configuredMonthlyContribution = Number(settingsData?.savings?.minMonthlyContribution) || 0;

  // ---- derived constants ----
  const monthlyContribution = useMemo(() => {
    if (configuredMonthlyContribution > 0) {
      return configuredMonthlyContribution;
    }
    const group = user?.group;
    if (group?.pricePerShare && group?.minShares) {
      return group.pricePerShare * group.minShares;
    }
    if (group?.solidarityAmount) {
      return group.solidarityAmount;
    }
    return 0;
  }, [configuredMonthlyContribution, user?.group]);

  // ---- API hooks ----
  const {
    data: contributionsRaw,
    isLoading: contributionsLoading,
  } = useQuery(
    ["member-dashboard-contributions", user?.id],
    async () => {
      console.log("[MemberDashboard] Fetching contributions...");
      const { data } = await api.get("/contributions/my?limit=500");
      console.log("[MemberDashboard] Contributions API response:", data);
      return (data.data || []) as any[];
    },
    {
      enabled: !!user?.id,
      onError: (err: any) => {
        console.error("[MemberDashboard] Contributions fetch error:", err);
        toast.error("Failed to load contributions data");
      },
    }
  );

  const {
    data: memberPaymentsRaw,
    isLoading: memberPaymentsLoading,
  } = useQuery(
    ["member-dashboard-payments", user?.id],
    async () => {
      const { data } = await api.get("/member-payments/my");
      return (data.data || []) as any[];
    },
    {
      enabled: !!user?.id,
      onError: (err: any) => {
        console.error("[MemberDashboard] Member payments fetch error:", err);
        toast.error("Failed to load savings data");
      },
    }
  );

  const {
    data: memberProfile,
    isLoading: memberProfileLoading,
  } = useQuery(
    ["member-dashboard-profile", user?.id],
    async () => {
      const { data } = await api.get("/members/me/profile");
      return data.data || data;
    },
    { enabled: !!user?.id, staleTime: 60000 }
  );

  const approvedSavings = useMemo(() => {
    // ALL contributions from /contributions/my (no status filter)
    const fromContributions = (contributionsRaw || [])
      .reduce((sum: number, c: any) => sum + (Number(c.amount) || Number(c.depositAmount) || 0), 0);
    // Approved savings from member-submitted payments
    const fromPayments = (memberPaymentsRaw || [])
      .filter((p: any) => p.type === "saving" && String(p.status || "").toLowerCase() === "approved")
      .reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);
    // Admin-set savingAmount from profile
    const memberSavingAmount = Number(memberProfile?.savingAmount || 0);
    return fromContributions + fromPayments + memberSavingAmount;
  }, [contributionsRaw, memberPaymentsRaw, memberProfile]);

  const {
    data: loansData,
    isLoading: loansLoading,
  } = useQuery(
    ["member-dashboard-loans", user?.id],
    async () => {
      console.log("[MemberDashboard] Fetching loans...");
      const { data } = await api.get("/loans/my");
      console.log("[MemberDashboard] Loans API response:", data);
      const r = data.data || {};
      const loans = Array.isArray(r.loans) ? r.loans : [];
      const statusLower = (s: string) => String(s || "").toLowerCase().replace(/\s+/g, "_");
      const active = loans.filter(
        (l: any) => ["active", "approved", "disbursed", "ready_for_disbursement", "approved_by_leaders"].includes(statusLower(l.status))
      );
      const pending = loans.filter((l: any) => ["pending", "pending_approval", "awaiting_approval"].includes(statusLower(l.status)));
      const approvedNotDisbursed = loans.filter((l: any) => statusLower(l.status) === "approved" || statusLower(l.status) === "approved_by_leaders");
      const completed = loans.filter((l: any) => ["completed", "fully_repaid"].includes(statusLower(l.status)));
      const now = new Date();
      const overdue = active.filter((l: any) => {
        const due = l.nextPaymentDate || l.dueDate;
        return due && new Date(due) < now;
      });
      const nextPaymentDate = active.length > 0
        ? active.reduce((earliest: Date | null, l: any) => {
          const d = l.nextPaymentDate || l.dueDate || null;
          if (!d) return earliest;
          const date = new Date(d);
          return !earliest || date < earliest ? date : earliest;
        }, null as Date | null)
        : null;
      const totalRepaidAll = loans.reduce((sum: number, l: any) => sum + (Number(l.totalPaid) || 0), 0);
      return {
        count: active.length,
        pendingCount: pending.length,
        approvedCount: approvedNotDisbursed.length,
        overdueCount: overdue.length,
        completedCount: completed.length,
        outstandingBalance: active.reduce(
          (sum: number, l: any) => sum + (Number(l.dueAmount) || Number(l.outstandingBalance) || Number(l.amount) || 0),
          0
        ),
        totalRepaid: totalRepaidAll,
        totalBorrowed: loans.reduce((sum: number, l: any) => sum + (Number(l.amount) || 0), 0),
        nextInstallment: active.length > 0
          ? Math.min(...active.map((l: any) => Number(l.monthlyPayment) || 0).filter((v: number) => v > 0))
          : 0,
        nextPaymentDate,
        hasLoans: active.length > 0,
        activeLoans: active,
      };
    },
    {
      enabled: !!user?.id,
      onError: (err: any) => {
        console.error("[MemberDashboard] Loans fetch error:", err);
        toast.error("Failed to load loan data");
      },
    }
  );

  const memberGroup = user?.group;

  console.log("[MemberDashboard] Member group:", memberGroup?.name);

  // ---- computed data ----
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const { byMonth, thisMonthTotal, thisYearTotal, monthsPaidThisYear, missedMonths, timeline, pendingCount, verifiedCount, prevMonthsTotal } = useMemo(() => {
    const list = contributionsRaw || [];
    console.log("[MemberDashboard] Computing dashboard from", list.length, "contributions");

    const map: Record<string, { total: number; verified: number; count: number }> = {};
    let pending = 0;
    let verified = 0;

    list.forEach((c: any) => {
      const d = new Date(c.contributionDate);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (!map[key]) map[key] = { total: 0, verified: 0, count: 0 };
      map[key].total += Number(c.amount) || 0;
      map[key].count += 1;
      const v = c.isVerified === true || c.status === "Verified" || c.status === "Completed";
      if (v) {
        verified++;
        map[key].verified += Number(c.amount) || 0;
      } else {
        pending++;
      }
    });

    const curKey = `${currentYear}-${currentMonth}`;
    const cur = map[curKey];
    const curMonthTotal = cur?.total || 0;

    let yearTotal = 0;
    let monthsPaid = 0;
    const missed: { month: number; year: number }[] = [];
    for (let m = 0; m <= currentMonth; m++) {
      const key = `${currentYear}-${m}`;
      const entry = map[key];
      if (entry && entry.total > 0) {
        yearTotal += entry.total;
        monthsPaid++;
      } else {
        missed.push({ month: m, year: currentYear });
      }
    }

    const prevTotal = yearTotal - curMonthTotal;

    const last6: { month: number; year: number; label: string; paid: boolean; amount: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(currentYear, currentMonth - i, 1);
      const m = d.getMonth();
      const y = d.getFullYear();
      const key = `${y}-${m}`;
      const entry = map[key];
      last6.push({
        month: m,
        year: y,
        label: `${MONTH_NAMES[m]} ${y}`,
        paid: !!(entry && entry.total > 0),
        amount: entry?.total || 0,
      });
    }

    return {
      byMonth: map,
      thisMonthTotal: curMonthTotal,
      thisYearTotal: yearTotal,
      monthsPaidThisYear: monthsPaid,
      missedMonths: missed,
      timeline: last6,
      pendingCount: pending,
      verifiedCount: verified,
      prevMonthsTotal: prevTotal,
    };
  }, [contributionsRaw, currentYear, currentMonth]);

  const totalSavings = approvedSavings;

  const currentMonthPaid = thisMonthTotal > 0;
  const monthsInYear = currentMonth + 1;
  const yearCompletionPct = Math.round((monthsPaidThisYear / monthsInYear) * 100);

  const activeLoan = loansData?.activeLoans?.[0];
  const loanRequestedAmount = Number(activeLoan?.amount) || loansData?.totalBorrowed || 0;
  const loanDisbursedAmount = Number(activeLoan?.disbursedAmount) || (loanRequestedAmount > 0 ? Math.round(loanRequestedAmount * 0.9) : 0);
  const outstandingBalance = loansData?.outstandingBalance || 0;
  const repaymentTotal = outstandingBalance + (loansData?.totalRepaid || 0);
  const repaymentProgress = repaymentTotal > 0 ? Math.round(((loansData?.totalRepaid || 0) / repaymentTotal) * 100) : 0;
  const salaryContributionsThisYear = thisYearTotal;
  const voluntarySavings = useMemo(() => {
    const fromPayments = (memberPaymentsRaw || [])
      .filter((p: any) => p.type === "saving" && String(p.status || "").toLowerCase() === "approved")
      .reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);
    const memberSavingAmount = Number(memberProfile?.savingAmount || 0);
    return fromPayments + memberSavingAmount;
  }, [memberPaymentsRaw, memberProfile]);
  const shareValue = configuredShareValue > 0 ? configuredShareValue : SHARE_VALUE;
  const totalShares = calculateShares(totalSavings);

  console.log("[MemberDashboard] Shares debug:", {
    memberId: user?.id,
    totalSavings,
    shareValue,
    calculatedShares: totalShares,
    configuredShareValue,
  });

  const nextDeductionDate = loansData?.nextPaymentDate || endOfMonth(now);

  const goToLoanDetails = () => {
    if (activeLoan?.loanCode || activeLoan?.id) {
      navigate(`/member/loans/${activeLoan.loanCode || activeLoan.id}`);
      return;
    }
    navigate("/member/loans");
  };

  const downloadStatement = () => {
    const rows = [
      ["Date", "Description", "Amount", "Status"],
      ...(contributionsRaw || []).map((c: any) => [
        format(new Date(c.contributionDate || c.createdAt || now), "yyyy-MM-dd"),
        c.description || "Savings Contribution",
        Number(c.amount || 0),
        c.status || (c.isVerified ? "Verified" : "Pending"),
      ]),
    ];
    const csv = rows
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `member-statement-${format(now, "yyyy-MM-dd")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const actions = [
    { label: "Make a Saving", description: "Add money anytime", icon: HandCoins, onClick: () => navigate("/member/savings/record"), color: "text-green-600", bg: "bg-green-50", border: "border-green-100" },
    { label: "View Statement", description: "Download statements", icon: FileText, onClick: () => navigate("/member/reports/statement"), color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-100" },
    { label: "Loan Details", description: "View repayment plan", icon: CreditCard, onClick: goToLoanDetails, color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-100" },
    { label: "Transaction History", description: "See all transactions", icon: History, onClick: () => navigate("/member/reports/statement"), color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-100" },
    { label: "My Profile", description: "Update your info", icon: UserCircle, onClick: () => navigate("/member/profile"), color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-100" },
  ];

  const recentActivityRows = useMemo(() => {
    const contributionRows = (contributionsRaw || []).map((c: any) => {
      const date = c.contributionDate || c.createdAt || now.toISOString();
      const status = c.status || (c.isVerified ? "Verified" : "Pending");
      const isAdminSaving = String(c.source || "").toLowerCase() === "manual_member_contribution";
      return {
        id: `contrib-${c.id || `${date}-${c.amount}`}`,
        date,
        description: isAdminSaving
          ? (c.notes || "Admin Saving Record")
          : (c.description || c.contributionType || "Savings Contribution"),
        type: isAdminSaving ? "Saving" : (c.contributionType || c.source || "Savings"),
        amount: Number(c.amount || c.depositAmount || 0),
        shares: Number(c.shareCount || 0),
        method: c.paymentMethod || c.method || (isAdminSaving ? "Cash" : "Salary Deduction"),
        status,
        reference: c.referenceNumber || c.reference || "-",
      };
    });

    const savingRows = (memberPaymentsRaw || [])
      .filter((p: any) => p.type === "saving" && String(p.status || "").toLowerCase() === "approved")
      .map((p: any) => {
        const date = p.paymentDate || p.createdAt || now.toISOString();
        return {
          id: `saving-${p.id}`,
          date,
          description: p.notes || "Voluntary Saving",
          type: "Saving",
          amount: Number(p.amount || 0),
          shares: Number(p.shares || 0),
          method: p.paymentMethod || "Manual Payment",
          status: "Approved",
          reference: p.approvedBy ? `Approved by ${p.approvedBy}` : "-",
        };
      });

    return [...contributionRows, ...savingRows]
      .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [contributionsRaw, memberPaymentsRaw, now]);

  const filteredRecentActivityRows = useMemo(() => {
    const q = recentActivitySearch.trim().toLowerCase();
    const filterValueFor = (id: string) => recentActivityColumnFilters.find((f) => f.id === id)?.value as string[] | undefined;
    const statusFilter = filterValueFor("status");
    const methodFilter = filterValueFor("method");

    return recentActivityRows.filter((row: any) => {
      const matchesSearch = !q || [
        row.description,
        row.type,
        row.method,
        row.status,
        row.reference,
        row.amount,
        format(new Date(row.date), "MMM dd, yyyy"),
      ]
        .join(" ")
        .toLowerCase()
        .includes(q);
      const matchesStatus = !statusFilter?.length || statusFilter.includes(row.status);
      const matchesMethod = !methodFilter?.length || methodFilter.includes(row.method);
      return matchesSearch && matchesStatus && matchesMethod;
    });
  }, [recentActivityRows, recentActivitySearch, recentActivityColumnFilters]);

  const recentActivityPageCount = Math.max(1, Math.ceil(filteredRecentActivityRows.length / recentActivityPagination.pageSize));
  const paginatedRecentActivityRows = useMemo(() => {
    const safePageIndex = Math.min(recentActivityPagination.pageIndex, recentActivityPageCount - 1);
    const start = safePageIndex * recentActivityPagination.pageSize;
    return filteredRecentActivityRows.slice(start, start + recentActivityPagination.pageSize);
  }, [filteredRecentActivityRows, recentActivityPageCount, recentActivityPagination.pageIndex, recentActivityPagination.pageSize]);

  const recentActivityColumns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "date",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
        cell: ({ row }) => (
          <div className="whitespace-nowrap text-sm font-medium text-slate-700">
            {format(new Date(row.getValue("date")), "MMM dd, yyyy")}
          </div>
        ),
      },
      {
        accessorKey: "description",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Activity" />,
        cell: ({ row }) => (
          <div className="min-w-[220px]">
            <p className="text-sm font-semibold text-slate-800">{row.getValue("description")}</p>
            <p className="text-xs text-slate-500">Ref: {row.original.reference}</p>
          </div>
        ),
      },
      {
        accessorKey: "type",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
        cell: ({ row }) => (
          <Badge variant="outline" className="border-blue-100 bg-blue-50 text-xs font-semibold text-blue-700">
            {row.getValue("type")}
          </Badge>
        ),
      },
      {
        accessorKey: "amount",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm font-bold text-slate-900">
            {Number(row.getValue("amount") || 0).toLocaleString()} FRW
          </span>
        ),
      },
      {
        accessorKey: "shares",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Shares" />,
        cell: ({ row }) => (
          <span className="text-sm font-semibold text-blue-700">
            {Number(row.getValue("shares") || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        accessorKey: "method",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Method" />,
        cell: ({ row }) => <span className="whitespace-nowrap text-sm text-slate-600">{row.getValue("method")}</span>,
      },
      {
        accessorKey: "status",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
        cell: ({ row }) => {
          const status = String(row.getValue("status") || "Pending");
          const normalized = status.toLowerCase();
          const statusClass = normalized.includes("verified") || normalized.includes("completed") || normalized.includes("paid")
            ? "border-green-200 bg-green-50 text-green-700"
            : normalized.includes("reject") || normalized.includes("cancel")
              ? "border-red-200 bg-red-50 text-red-700"
              : "border-amber-200 bg-amber-50 text-amber-700";
          return (
            <Badge variant="outline" className={`${statusClass} text-xs font-semibold`}>
              {status}
            </Badge>
          );
        },
      },
      {
        id: "actions",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Action" />,
        cell: () => (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs font-semibold text-[#009A44] hover:bg-green-50 hover:text-[#008238]"
            onClick={() => navigate("/member/reports/statement")}
          >
            View
            <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        ),
      },
    ],
    [navigate]
  );

  const recentActivityFacets = [
    {
      name: "status",
      title: "Status",
      type: "checkbox",
      options: [
        { label: "Verified", value: "Verified" },
        { label: "Completed", value: "Completed" },
        { label: "Pending", value: "Pending" },
        { label: "Rejected", value: "Rejected" },
      ],
    },
    {
      name: "method",
      title: "Method",
      type: "checkbox",
      options: [
        { label: "Salary Deduction", value: "Salary Deduction" },
        { label: "Cash", value: "Cash" },
        { label: "Bank Transfer", value: "Bank Transfer" },
        { label: "Mobile Money", value: "Mobile Money" },
      ],
    },
  ];

  const isLoading = contributionsLoading || loansLoading || settingsLoading || memberPaymentsLoading;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="rounded-xl bg-gradient-to-r from-[#009A44] to-[#007a35] p-6 animate-pulse">
          <div className="h-8 w-48 bg-white/20 rounded mb-2" />
          <div className="h-4 w-96 bg-white/10 rounded" />
          <div className="flex gap-2 mt-6">
            <div className="h-16 w-36 bg-white/15 rounded-lg" />
            <div className="h-16 w-36 bg-white/15 rounded-lg" />
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-[14px] border border-gray-200 bg-white p-4 animate-pulse">
              <div className="h-3 w-20 bg-gray-200 rounded mb-2" />
              <div className="h-6 w-24 bg-gray-200 rounded mb-1" />
              <div className="h-3 w-10 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          <div className="lg:col-span-8 rounded-xl border border-slate-100 bg-white p-4 animate-pulse">
            <div className="h-4 w-48 bg-gray-200 rounded mb-3" />
            <div className="h-3 bg-gray-100 rounded-full mb-3" />
            <div className="grid grid-cols-3 gap-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-14 bg-gray-100 rounded-lg" />
              ))}
            </div>
          </div>
          <div className="lg:col-span-4 rounded-xl border border-slate-100 bg-white p-4 animate-pulse">
            <div className="grid grid-cols-2 gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-12 bg-gray-100 rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden rounded-lg border-0 bg-gradient-to-r from-[#009A44] to-[#007a35] shadow-sm">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">Welcome back, {getLastName(user)} 👋</h1>
            <p className="mt-1 text-sm text-white/85">Track your savings, loan applications, repayments, and cooperative activities.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:min-w-[300px]">
            <div className="rounded-md bg-white/15 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase text-white/70">Savings & Contributions</p>
              <p className="text-sm font-bold text-white">{totalSavings.toLocaleString()} FRW</p>
            </div>
            <div className="rounded-md bg-white/15 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase text-white/70">Loan</p>
              <p className="text-sm font-bold text-white">{outstandingBalance.toLocaleString()} FRW</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <Card className="rounded-lg border-green-100 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100">
                <HandCoins className="h-4 w-4 text-green-700" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-slate-900">Amount in Account (All Savings & Contributions)</p>
                <p className="mt-1 text-xl font-bold text-green-600">{totalSavings.toLocaleString()} FRW</p>
                <p className="mt-0.5 text-xs font-medium text-slate-500">Savings, contributions & admin-recorded deposits</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-lg border-blue-100 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100">
                <Layers className="h-4 w-4 text-blue-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-slate-900">Total Shares</p>
                <p className="mt-1 text-xl font-bold text-blue-600">{Number(totalShares || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} Shares</p>
                <p className="mt-0.5 text-xs font-medium text-slate-500">From all contributions & savings</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="inline-flex rounded-md bg-blue-50 px-2 py-1 text-[11px] font-semibold text-slate-600">1 Share = {Number(shareValue || 0).toLocaleString()} FRW</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-lg border-orange-100 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-100">
                <Calendar className="h-4 w-4 text-orange-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-slate-900">Monthly Contribution</p>
                <p className="mt-1 text-xl font-bold text-orange-600">{monthlyContribution.toLocaleString()} FRW</p>
                <p className="mt-0.5 text-xs font-medium text-slate-500">Deducted from salary</p>
                <span className={`mt-2 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-bold ${currentMonthPaid ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                  {currentMonthPaid ? `Paid for ${format(now, "MMMM yyyy")}` : `Not paid for ${format(now, "MMMM yyyy")}`}
                  {currentMonthPaid ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-lg border-purple-100 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-purple-100">
                <WalletCards className="h-4 w-4 text-purple-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-slate-900">Active Loan</p>
                <p className="mt-1 text-xl font-bold text-purple-600">{outstandingBalance.toLocaleString()} FRW</p>
                <p className="mt-0.5 text-xs font-medium text-slate-500">Outstanding Balance</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-lg border-green-100 bg-green-50/40 shadow-sm">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <Info className="mt-0.5 h-5 w-5 text-green-700" />
            <p className="text-sm leading-6 text-slate-700">
              <span className="font-bold text-green-700">Total Shares ({Number(totalShares || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} Shares)</span> are calculated from all your contributions and savings.
              <br />
              Any contribution (salary or savings) increases your shares.
            </p>
          </div>
          <button onClick={() => navigate("/member/reports/savings")} className="flex items-center gap-2 text-sm font-bold text-green-700">
            How Shares Work <ArrowRight className="h-4 w-4" />
          </button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="rounded-lg border-slate-100 shadow-sm">
          <CardHeader className="p-5 pb-2">
            <CardTitle className="flex items-center gap-3 text-base text-slate-900">
              <Calendar className="h-5 w-5" /> Monthly Contribution (Salary Deduction)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 pt-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 rounded-lg border border-slate-100 p-4">
              <div>
                <p className="text-xs font-medium text-slate-500">Monthly Commitment</p>
                <p className="mt-2 text-lg font-bold text-slate-900">{monthlyContribution.toLocaleString()} FRW</p>
              </div>
              <div className="mt-4 border-slate-100 sm:mt-0 sm:border-l sm:pl-8">
                <p className="text-xs font-medium text-slate-500">This Month ({format(now, "MMMM")})</p>
                <p className={`mt-2 flex items-center gap-2 text-lg font-bold ${currentMonthPaid ? "text-green-700" : "text-red-600"}`}>
                  {currentMonthPaid ? "Paid" : "Not Paid"} {currentMonthPaid ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                </p>
              </div>
              <div className="mt-4 border-slate-100 sm:mt-0 sm:border-l sm:pl-8">
                <p className="text-xs font-medium text-slate-500">Next Deduction</p>
                <p className="mt-2 text-lg font-bold text-slate-900">{format(nextDeductionDate, "dd MMM yyyy")}</p>
              </div>
            </div>
            <div className="mt-4 rounded-lg border border-green-100 bg-green-50/70 p-4">
              <div className="flex gap-3">
                <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-700" />
                <div>
                  <p className="text-sm font-bold text-green-800">
                    {currentMonthPaid ? `Your contribution for ${format(now, "MMMM yyyy")} has been successfully deducted.` : `Your contribution for ${format(now, "MMMM yyyy")} is not yet paid.`}
                  </p>
                  <p className="mt-1 text-sm text-slate-600">{currentMonthPaid ? "Thank you for staying consistent!" : "Record a saving or check your contribution status."}</p>
                </div>
              </div>
              <button onClick={() => navigate("/member/contributions")} className="mt-4 flex items-center gap-2 text-sm font-bold text-green-700">
                View Contribution History <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-lg border-slate-100 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between p-5 pb-2">
            <CardTitle className="flex items-center gap-3 text-base text-slate-900">
              <WalletCards className="h-5 w-5" /> Loan Overview
            </CardTitle>
            <button onClick={goToLoanDetails} className="flex items-center gap-2 text-sm font-bold text-green-700">
              View Full Loan Details <ArrowRight className="h-4 w-4" />
            </button>
          </CardHeader>
          <CardContent className="p-5 pt-3">
            <div className="rounded-lg border border-slate-100 p-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <p className="text-xs font-medium text-slate-500">Loan Amount Requested</p>
                  <p className="mt-2 text-base font-bold text-slate-900">{loanRequestedAmount.toLocaleString()} FRW</p>
                </div>
                <div className="border-slate-100 sm:border-l sm:pl-8">
                  <p className="text-xs font-medium text-slate-500">Amount Disbursed (After 10% interest)</p>
                  <p className="mt-2 text-base font-bold text-green-700">{loanDisbursedAmount.toLocaleString()} FRW</p>
                </div>
                <div className="border-slate-100 sm:border-l sm:pl-8">
                  <p className="text-xs font-medium text-slate-500">Outstanding Balance</p>
                  <p className="mt-2 text-base font-bold text-red-600">{outstandingBalance.toLocaleString()} FRW</p>
                </div>
              </div>
              <div className="mt-5">
                <div className="mb-2 flex justify-between text-xs font-semibold text-slate-500">
                  <span>Repayment Progress</span>
                  <span>{repaymentProgress}% Repaid</span>
                </div>
                <Progress value={repaymentProgress} className="h-2 bg-slate-100 [&>div]:bg-[#009A44]" />
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <p className="text-xs font-medium text-slate-500">Monthly Installment</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">{(loansData?.nextInstallment || 0).toLocaleString()} FRW</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500">Next Payment</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">{loansData?.nextPaymentDate ? format(loansData.nextPaymentDate, "dd MMM yyyy") : "-"}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500">Remaining Months</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">{activeLoan?.remainingMonths || "-"}</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Button variant="outline" className="h-9 border-green-600 text-green-700 hover:bg-green-50" onClick={() => navigate("/member/loans/declare-payment")}>
                  Repay Loan
                </Button>
                <Button className="h-9 bg-[#009A44] text-white hover:bg-[#008238]" onClick={() => navigate("/member/loans/declare-payment")}>
                  Repay All ({outstandingBalance.toLocaleString()} FRW)
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-lg border-slate-100 shadow-sm">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="flex items-center gap-3 text-base text-slate-900">
            <ReceiptText className="h-5 w-5 text-slate-700" /> Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 p-4 pt-2 sm:grid-cols-2 lg:grid-cols-5">
          {actions.map((a) => (
            <button key={a.label} onClick={a.onClick} className={`flex items-center gap-3 rounded-lg border ${a.border} px-4 py-3 text-left transition hover:shadow-sm`}>
              <span className={`flex h-10 w-10 items-center justify-center rounded-full ${a.bg} ${a.color}`}>
                <a.icon className="h-5 w-5" />
              </span>
              <span>
                <span className={`block text-sm font-bold ${a.color}`}>{a.label}</span>
                <span className="block text-xs text-slate-500">{a.description}</span>
              </span>
            </button>
          ))}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="rounded-lg border-slate-100 shadow-sm">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="flex items-center gap-3 text-base text-slate-900">
              <ReceiptText className="h-5 w-5" /> Account Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 p-4 pt-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Salary Contributions (This Year)</span><span className="font-bold">{salaryContributionsThisYear.toLocaleString()} FRW</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Voluntary Savings</span><span className="font-bold">{voluntarySavings.toLocaleString()} FRW</span></div>
            <div className="flex justify-between text-green-700"><span>Total in Account (Savings)</span><span className="font-bold">{totalSavings.toLocaleString()} FRW</span></div>
            <div className="my-2 border-t border-slate-100" />
            <div className="flex justify-between"><span className="text-slate-600">Total Shares</span><span className="font-bold text-blue-600">{Number(totalShares || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} Shares</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Share Value (1 Share = {Number(shareValue || 0).toLocaleString()} FRW)</span><span className="font-bold">{totalSavings.toLocaleString()} FRW</span></div>
            <button onClick={() => navigate("/member/reports/savings")} className="mx-auto mt-3 flex items-center gap-2 text-sm font-bold text-green-700">
              View Savings Details <ArrowRight className="h-4 w-4" />
            </button>
          </CardContent>
        </Card>

        <Card className="rounded-lg border-slate-100 shadow-sm">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="flex items-center gap-3 text-base text-slate-900">
              <WalletCards className="h-5 w-5" /> Loan Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 p-4 pt-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Loan Amount Requested</span><span className="font-bold">{loanRequestedAmount.toLocaleString()} FRW</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Amount Disbursed (After Interest 10%)</span><span className="font-bold">{loanDisbursedAmount.toLocaleString()} FRW</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Interest Deducted (10%)</span><span className="font-bold">{Math.max(loanRequestedAmount - loanDisbursedAmount, 0).toLocaleString()} FRW</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Total Repaid</span><span className="font-bold">{(loansData?.totalRepaid || 0).toLocaleString()} FRW</span></div>
            <div className="flex justify-between text-red-600"><span>Outstanding Balance</span><span className="font-bold">{outstandingBalance.toLocaleString()} FRW</span></div>
            <button onClick={goToLoanDetails} className="mx-auto mt-3 flex items-center gap-2 text-sm font-bold text-green-700">
              View Loan Details <ArrowRight className="h-4 w-4" />
            </button>
          </CardContent>
        </Card>

        <Card className="rounded-lg border-slate-100 shadow-sm">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="flex items-center gap-3 text-base text-slate-900">
              <FileText className="h-5 w-5" /> Latest Statement
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-2">
            <div className="rounded-lg border border-slate-100 bg-slate-50 p-5 text-center">
              <p className="text-sm text-slate-600">Your latest statement is up to date.</p>
              <p className="mt-1 text-sm text-slate-600">Period: {format(new Date(currentYear, 0, 1), "dd MMM yyyy")} - {format(now, "dd MMM yyyy")}</p>
              <Button variant="outline" className="mt-5 w-full border-green-600 text-green-700 hover:bg-green-50" onClick={downloadStatement}>
                <Download className="mr-2 h-4 w-4" /> View / Download Statement
              </Button>
              <button onClick={() => navigate("/member/reports/statement")} className="mx-auto mt-4 flex items-center gap-2 text-sm font-bold text-green-700">
                All Statements <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      <section className="space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
              <History className="h-5 w-5 text-slate-600" />
              Recent Activity
            </h2>
            <p className="text-sm text-slate-500">Latest savings and contribution movements on your account.</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-fit text-sm font-bold text-green-700 hover:bg-green-50 hover:text-green-800"
            onClick={() => navigate("/member/reports/statement")}
          >
            View All Activity
            <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
        <DataTable
          columns={recentActivityColumns}
          data={paginatedRecentActivityRows}
          facets={recentActivityFacets}
          isLoading={contributionsLoading}
          isFetching={false}
          onSearch={(value: string) => {
            setRecentActivitySearch(value);
            setRecentActivityPagination((prev) => ({ ...prev, pageIndex: 0 }));
          }}
          columnFilters={recentActivityColumnFilters}
          setColumnFilters={(next) => {
            setRecentActivityColumnFilters(next);
            setRecentActivityPagination((prev) => ({ ...prev, pageIndex: 0 }));
          }}
          sorting={recentActivitySorting}
          setSorting={setRecentActivitySorting}
          setPagination={setRecentActivityPagination}
          pageIndex={Math.min(recentActivityPagination.pageIndex, recentActivityPageCount - 1)}
          pageSize={recentActivityPagination.pageSize}
          pageCount={recentActivityPageCount}
          enableExport={false}
          emptyMessage="No recent activity"
          emptyDescription="Your latest savings and contribution activity will appear here."
          loadingRows={4}
        />
      </section>
    </div>
  );

}
